import React, {
  useCallback, useState, useEffect, useContext,
} from 'react';
import * as PropTypes from 'prop-types';
import { Link, withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import '../login/Login.css';
import './ChangeEmail.css';
import {
  Form, Icon, Input, Button, Row, Divider, Typography, Spin,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import routeLocations from '../../utility/route-locations';
import ViaValidationAlert from '../../components/fields/ViaValidationAlert';
import { requestChangeEmail } from '../../state/ui/configuration/users/emailUiActions';
import { selectChangeEmailRequest } from '../../state/ui/configuration/users/emailUiReducer';
import { selectLoadUnauthUserRequest } from '../../state/ui/configuration/users/passwordUiReducer';
import { requestUnauthUser } from '../../state/ui/configuration/users/passwordUiActions';
import { ViaLocaleContext } from '../../contexts/ViaLanguageProvider';
import { ViaLogo } from '../../components/logo/ViaLogo';

const { Text } = Typography;


const createUsernameField = (form, intl) => form.getFieldDecorator('username', {
  rules: [
  ],
})(
  <Input
    prefix={<Icon type="user-delete" style={{ color: 'rgba(0,0,0,0.25)', fontSize: '18px' }} />}
    type="email"
    disabled
    placeholder={intl.formatMessage({ id: 'email' })}
    maxLength={80}
  />,
);

const createEmailField = (form, intl) => form.getFieldDecorator('newEmail', {
  rules: [
  ],
})(
  <Input
    prefix={<Icon type="user-add" style={{ color: 'rgba(0,0,0,0.25)', fontSize: '18px' }} />}
    type="email"
    disabled
    placeholder={intl.formatMessage({ id: 'displayName' })}
    maxLength={80}
  />,
);

// Component for allowing a user to change their password without knowing their original password.
function ChangeEmail({
  form, location, loading, changeEmailOnServer, requestError, history, loadUserForRequest, requestUser, loadUserError,
}) {
  const intl = useIntl();
  const { toggleLocale } = useContext(ViaLocaleContext);
  const [textVisible, setTextVisible] = useState(false);
  const suffixIcon = textVisible
    ? (<Icon type="eye-invisible" onClick={() => setTextVisible(false)} style={{ color: 'rgba(0,0,0,.5)' }} />)
    : (<Icon type="eye" onClick={() => setTextVisible(true)} style={{ color: 'rgba(0,0,0,.5)' }} />);
  const fieldType = textVisible ? 'text' : 'password';

  const tokenstart = location.pathname.lastIndexOf('/change-email') + 13; // want what is after this
  const requestToken = location.pathname.slice(tokenstart, location.pathname.length);

  useEffect(() => {
    if (!requestUser) {
      loadUserForRequest(requestToken, 'tokenExpired');
    } else {
      toggleLocale(requestUser.user.locale);
    }
  }, [requestToken, loadUserForRequest, requestUser, toggleLocale]);


  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        changeEmailOnServer(requestToken, values.username, values.password, history);
      }
    });
  }, [form, changeEmailOnServer, requestToken, history]);

  if (loadUserError || !requestToken || requestToken.length < 10) {
    return <Redirect to={routeLocations.login} />;
  }

  return !requestUser
    ? (
      <div className="app-login-layout">
        <Row type="flex" align="middle" justify="center">
          <div className="app-login-content">
            <Row type="flex" justify="center">
              <Spin size="large" />
            </Row>
          </div>
        </Row>
      </div>
    ) : (
      <div className="app-login-layout">
        <Row type="flex" align="middle" justify="center">
          <div className="app-login-content">
            <Row type="flex" justify="center">
              <ViaLogo href={routeLocations.landing} className="app-login-logo" />
            </Row>
            <div className="change-email-text">
              <Text>
                <FormattedMessage id="greeting" />
                ,&nbsp;
                {requestUser.user.displayName}
                .&nbsp;
                <FormattedMessage id="confirmEmail" />
              </Text>
            </div>
            <Row type="flex" justify="center">
              <ViaValidationAlert validationError={requestError} closable className="registration-checkbox-alert" />
            </Row>
            <Row>
              <Form onSubmit={e => handleSubmit(e)} className="app-login-form">
                <Form.Item>
                  { createUsernameField(form, intl) }
                </Form.Item>
                <Form.Item>
                  { createEmailField(form, intl) }
                </Form.Item>
                <Form.Item required>
                  {
                    form.getFieldDecorator('password', {
                      rules: [
                        { required: true, message: intl.formatMessage({ id: 'validationPassword' }) },
                      ],
                    })(
                      <Input
                        prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)', fontSize: '18px' }} />}
                        type={fieldType}
                        placeholder={intl.formatMessage({ id: 'password' })}
                        maxLength={80}
                        suffix={suffixIcon}
                      />,
                    )
                  }
                </Form.Item>
                <Form.Item>
                  <Button
                    className="change-email-button"
                    type="primary"
                    block
                    htmlType="submit"
                    loading={loading}
                  >
                    <FormattedMessage id="confirm" />
                  </Button>
                </Form.Item>
              </Form>
            </Row>
            <Divider className="change-email-divider" />
            <Row type="flex" justify="center">
              <Link className="login-form-sign-up" to={routeLocations.login}><FormattedMessage id="oldEmailLogin" /></Link>
            </Row>
          </div>
        </Row>
      </div>
    );
}


ChangeEmail.propTypes = {
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  location:
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  changeEmailOnServer: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  requestError: PropTypes.shape({
    code: PropTypes.number.isRequired,
    errorMessage: PropTypes.string.isRequired,
    errors: PropTypes.array,
  }),
  loadUserForRequest: PropTypes.func.isRequired,
  requestUser: PropTypes.shape({
    user: PropTypes.shape({
      username: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      locale: PropTypes.string.isRequired,
    }).isRequired,
    emailSentTo: PropTypes.string.isRequired,
  }),
  loadUserError: PropTypes.shape({
    code: PropTypes.number.isRequired,
    errorMessage: PropTypes.string.isRequired,
    errors: PropTypes.array,
  }),
};

ChangeEmail.defaultProps = {
  loading: false,
  requestError: null,
  loadUserError: null,
  requestUser: null,
};

const mapStateToProps = (state) => {
  const changeEmailRequest = selectChangeEmailRequest(state);
  const loadUserRequest = selectLoadUnauthUserRequest(state);
  return {
    loading: changeEmailRequest ? changeEmailRequest.isLoading : false,
    requestError: changeEmailRequest ? changeEmailRequest.error : null,
    loadUserError: loadUserRequest ? loadUserRequest.error : null,
    requestUser: loadUserRequest ? loadUserRequest.payload : null,

  };
};

const mapDispatchToProps = dispatch => ({
  changeEmailOnServer: (requestToken, username, password, history) => dispatch(requestChangeEmail(requestToken, username, password, history)),
  loadUserForRequest: (requestToken, failDescId) => dispatch(requestUnauthUser(requestToken, failDescId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  Form.create({
    name: 'change_email_page',
    onFieldsChange() {
    },
    mapPropsToFields({ requestUser }) {
      return {
        username: Form.createFormField({
          value: requestUser ? requestUser.user.username : null,
        }),
        newEmail: Form.createFormField({
          value: requestUser ? requestUser.emailSentTo : null,
        }),
      };
    },
  })(
    withRouter(ChangeEmail),
  ),
);
